import React from 'react';
import { Box, IconButton, Tooltip, SxProps, Theme } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface HelpIconProps {
  tooltipText: string;
  onToggle: () => void;
}

interface InfoBoxProps {
  children: React.ReactNode;
  onDismiss: () => void;
  shouldShow: boolean;
  sx?: SxProps<Theme>;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

// Component for the help icon that goes in the card header
export const HelpIcon: React.FC<HelpIconProps> = ({
  tooltipText,
  onToggle,
}) => {
  return (
    <Tooltip
      title={tooltipText}
      placement="right"
      arrow
      componentsProps={{ tooltip: { sx: { maxWidth: 400, p: 2 } } }}
    >
      <HelpOutlineIcon
        sx={{
          ml: 1,
          color: 'text.secondary',
          cursor: 'pointer',
          fontSize: 20,
        }}
        onClick={onToggle}
        data-tour="help-tooltips"
      />
    </Tooltip>
  );
};

// Component for the blue info box that goes in the card content (theme-aware)
export const InfoBox: React.FC<InfoBoxProps> = ({
  children,
  onDismiss,
  shouldShow,
  sx = {},
  leftIcon,
  rightIcon,
}) => {
  if (!shouldShow) {
    return null;
  }

  return (
    <Box
      sx={[
        (theme) => {
          const info = theme.palette.info;
          return {
            background:
              theme.palette.mode === 'dark' ? info.light : info.lighter,
            borderLeft: `4px solid ${
              theme.palette.mode === 'dark'
                ? theme.palette.info.main
                : theme.palette.info.light
            }`,
            borderRadius: 1,
            p: 2,
            mb: 2,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {leftIcon && leftIcon}
      <Box
        sx={(theme) => ({
          color:
            theme.palette.mode === 'dark'
              ? theme.palette.info.darker
              : theme.palette.info.dark,
          fontSize: '0.875rem',
          lineHeight: 1.43,
          flex: 1,
        })}
      >
        {children}
      </Box>
      {rightIcon && rightIcon}
      <IconButton
        size="small"
        aria-label="Dismiss info"
        onClick={onDismiss}
        sx={(theme) => ({
          position: 'absolute',
          top: 4,
          right: 4,
          color:
            theme.palette.mode === 'dark'
              ? theme.palette.info.dark
              : theme.palette.info.main,
        })}
      >
        ×
      </IconButton>
    </Box>
  );
};
